import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trophy, HelpCircle, Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const COLOR_MODES = [
  { id: 'blue', name: 'Ocean Blues', baseHue: 200, range: 60 },
  { id: 'green', name: 'Forest Greens', baseHue: 120, range: 60 },
  { id: 'orange-red', name: 'Sunset', baseHue: 15, range: 45 },
  { id: 'purple', name: 'Purple Haze', baseHue: 280, range: 50 }
];

const GradientSort = () => {
  const navigate = useNavigate();
  const [colors, setColors] = useState([]);
  const [moves, setMoves] = useState(0);
  const [isSolved, setIsSolved] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [colorMode, setColorMode] = useState(COLOR_MODES[0]);
  const [showClue, setShowClue] = useState(null);
  const [difficulty, setDifficulty] = useState(8); // 8 tiles is easier

  useEffect(() => {
    initPuzzle();
  }, [colorMode, difficulty]);

  const initPuzzle = () => {
    const gradient = [];
    const numTiles = difficulty;
    
    for (let i = 0; i < numTiles; i++) {
      // Create gradient within the color mode's hue range
      const hue = colorMode.baseHue + (i * (colorMode.range / (numTiles - 1))) - (colorMode.range / 2);
      // Also vary lightness for more obvious differences
      const lightness = 40 + (i * (30 / (numTiles - 1)));
      gradient.push({ 
        id: i, 
        hue: ((hue % 360) + 360) % 360, 
        lightness,
        sorted: i 
      });
    }
    
    // Shuffle the array
    const shuffled = [...gradient].sort(() => Math.random() - 0.5);
    
    // Make sure it's not already solved
    while (checkSolved(shuffled)) {
      shuffled.sort(() => Math.random() - 0.5);
    }
    
    setColors(shuffled);
    setMoves(0);
    setIsSolved(false);
    setSelectedIndex(null);
    setShowClue(null);
  };

  const checkSolved = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].sorted !== i) return false;
    }
    return true;
  };

  const handleTileClick = (index) => {
    if (isSolved) return;
    
    // If showing a clue for this tile, clear it
    if (showClue === index) {
      setShowClue(null);
    }
    
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else if (selectedIndex === index) {
      // Clicking the already-selected tile deselects it (no move counted)
      setSelectedIndex(null);
    } else {
      const newColors = [...colors];
      [newColors[selectedIndex], newColors[index]] = [newColors[index], newColors[selectedIndex]];
      setColors(newColors);
      setMoves(moves + 1);
      setSelectedIndex(null);
      
      if (checkSolved(newColors)) {
        setIsSolved(true);
      }
    }
  };

  const getClue = (index) => {
    // Find where this tile should go
    const tile = colors[index];
    const correctPosition = tile.sorted;
    setShowClue(index);
    
    // Auto-hide clue after 3 seconds (only if a newer clue hasn't replaced it)
    setTimeout(() => {
      setShowClue(prev => (prev === index ? null : prev));
    }, 3000);
  };

  const getGridCols = () => {
    if (difficulty <= 6) return 'grid-cols-3 md:grid-cols-6';
    if (difficulty <= 8) return 'grid-cols-4 md:grid-cols-4';
    return 'grid-cols-4 md:grid-cols-6';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#FFE6F0] relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-1">Gradient Sort</h1>
            <p className="text-lg text-[#4A5568]">Moves: <span className="font-bold text-[#81B29A]">{moves}</span></p>
          </div>
          
          <button
            data-testid="reset-puzzle"
            onClick={initPuzzle}
            className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </header>

        {/* Color Mode Selector */}
        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {COLOR_MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => setColorMode(mode)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                colorMode.id === mode.id
                  ? 'bg-[#81B29A] text-white'
                  : 'bg-white text-[#4A5568] hover:bg-slate-100'
              }`}
            >
              {mode.name}
            </button>
          ))}
        </div>

        {/* Difficulty Selector */}
        <div className="mb-6 flex justify-center gap-2">
          <span className="text-[#4A5568] mr-2 self-center">Tiles:</span>
          {[6, 8, 12].map(num => (
            <button
              key={num}
              onClick={() => setDifficulty(num)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                difficulty === num
                  ? 'bg-[#2D3748] text-white'
                  : 'bg-white text-[#4A5568] hover:bg-slate-100'
              }`}
            >
              {num}
            </button>
          ))}
        </div>

        <AnimatePresence>
          {isSolved && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="bg-white rounded-3xl p-6 shadow-2xl border-4 border-green-500 mb-8 text-center"
            >
              <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-green-600 mb-2">Perfect!</h2>
              <p className="text-xl text-[#4A5568]">You solved it in {moves} moves!</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-slate-100">
          <div className={`grid ${getGridCols()} gap-3 md:gap-4`}>
            {colors.map((color, index) => (
              <div key={color.id} className="relative">
                <motion.button
                  data-testid={`color-tile-${index}`}
                  onClick={() => handleTileClick(index)}
                  className={`aspect-square rounded-2xl w-full transition-all duration-300 ${
                    selectedIndex === index ? 'ring-4 ring-[#81B29A] scale-110 z-10' : 'hover:scale-105'
                  } ${isSolved ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{ 
                    backgroundColor: `hsl(${color.hue}, 70%, ${color.lightness}%)`,
                    boxShadow: selectedIndex === index ? '0 10px 30px rgba(0,0,0,0.2)' : '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                  whileHover={{ scale: isSolved ? 1 : 1.05 }}
                  whileTap={{ scale: isSolved ? 1 : 0.95 }}
                />
                
                {/* Clue button */}
                {!isSolved && (
                  <button
                    onClick={(e) => { e.stopPropagation(); getClue(index); }}
                    className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 w-7 h-7 rounded-full flex items-center justify-center shadow-md hover:bg-yellow-300 transition-colors"
                    title="Get a clue"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                )}
                
                {/* Show clue indicator */}
                <AnimatePresence>
                  {showClue === index && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-[#2D3748] text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap z-20"
                    >
                      <Lightbulb className="w-4 h-4 inline mr-1" />
                      Goes in position {color.sorted + 1}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* Reference gradient */}
        <div className="mt-8">
          <p className="text-center text-[#4A5568] mb-3">Goal: Arrange from dark to light</p>
          <div className="h-8 rounded-full overflow-hidden flex">
            {Array.from({ length: difficulty }).map((_, i) => {
              const hue = colorMode.baseHue + (i * (colorMode.range / (difficulty - 1))) - (colorMode.range / 2);
              const lightness = 40 + (i * (30 / (difficulty - 1)));
              return (
                <div
                  key={i}
                  className="flex-1"
                  style={{ backgroundColor: `hsl(${((hue % 360) + 360) % 360}, 70%, ${lightness}%)` }}
                />
              );
            })}
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[#4A5568]">
            Tap two tiles to swap them. Click the ? on any tile for a hint!
          </p>
        </div>
      </div>
    </div>
  );
};

export default GradientSort;
